import { useEffect, useState, type ReactNode } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LogoutIcon from "@mui/icons-material/Logout";

import {
  adminLogin,
  adminLogout,
  hasRole,
  LOGOUT_EVENT,
  type AdminRole,
} from "../services/adminAuth";

type Props = {
  role: AdminRole;
  children: ReactNode;
};

export default function AdminGate({ role, children }: Props) {
  const [allowed, setAllowed] = useState(() => hasRole(role));
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onLogout = () => setAllowed(false);

    window.addEventListener(LOGOUT_EVENT, onLogout);
    return () => window.removeEventListener(LOGOUT_EVENT, onLogout);
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");

    try {
      await adminLogin(password);

      if (hasRole(role)) {
        setAllowed(true);
        setPassword("");
      } else {
        setError("Ce mot de passe ne donne pas accès à cette page.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Connexion impossible");
    } finally {
      setSubmitting(false);
    }
  };

  if (allowed) {
    return (
      <>
        <Box sx={{ maxWidth: 1400, mx: "auto", px: 3, pt: 2, textAlign: "right" }}>
          <Button size="small" startIcon={<LogoutIcon />} onClick={adminLogout}>
            Se déconnecter
          </Button>
        </Box>
        {children}
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: 2, py: 8 }}>
      <Card sx={{ width: "100%", maxWidth: 380 }}>
        <CardContent
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Stack spacing={2} alignItems="center">
            <LockIcon color="action" />
            <Typography variant="h6">Accès réservé</Typography>

            <TextField
              id="admin-password"
              autoFocus
              fullWidth
              type="password"
              label="Mot de passe"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {error && (
              <Alert severity="error" sx={{ width: "100%" }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              color="error"
              fullWidth
              disabled={!password || submitting}
            >
              {submitting ? "Connexion..." : "Se connecter"}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
